import React, { ReactElement, useEffect, useState } from 'react';
import { Box, List, ListItem, SecondaryButton, Text } from '@cko/primitives';
import { trackEvent } from '@cko/dashboard-shared';
import styled from 'styled-components';
import InfiniteScrollComponent from 'react-infinite-scroll-component';

import { useNotifications } from '../../../contexts/NotificationsProvider';
import { Notification } from '../../../types';
import { LoadingItem } from '../LoadingItem';
import { NavigationItem } from './NavigationItem';
import { ItemView } from './ItemView';

const SCROLLABLE_CONTAINER_ID = 'notifications-in-navigation-scrollable-container';
const LIST_VIEW_TEST_IDS = {
  infiniteScrollErrorMessage: 'notifications-in-navigation-infinite-scroll-error-message',
  infiniteScrollReloadButton: 'notifications-in-navigation-infinite-scroll-reload-button',
};

const ITEM_COMPONENTS: Record<string, (props: any) => ReactElement> = {
  navigation: NavigationItem,
};

const Wrapper = styled(List)`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 var(--layout-spoon) var(--layout-spoon);
  width: 100%;
`;

const ErrorWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  margin: 0 auto;
  padding: var(--layout-spoon);
`;

type Props = {
  closeList: () => void;
  notifications: Notification[];
};

export const ListView = ({ closeList, notifications }: Props) => {
  const { canFetchMore, fetchNextBatchFromId, updateLastSeenTimestamp } = useNotifications();
  const oldestNotification = notifications[notifications.length - 1];
  const [isError, setIsError] = useState(false);

  const handleInfiniteScroll = async () => {
    trackEvent('Scroll - in-dashboard notifications - fetch previous notifications in list', {});
    try {
      await fetchNextBatchFromId(oldestNotification.eventId, oldestNotification.createdTimestamp);
    } catch (error) {
      setIsError(true);
    }
  };

  const handleReloadButtonClick = async () => {
    setIsError(false);
    trackEvent(
      'Click - in-dashboard notifications - reload button, fetch previous notifications in list',
      {},
    );
    try {
      await fetchNextBatchFromId(oldestNotification.eventId, oldestNotification.createdTimestamp);
    } catch (error) {
      setIsError(true);
    }
  };

  useEffect(() => {
    updateLastSeenTimestamp();
  }, [notifications.length]);

  return (
    <Wrapper id={SCROLLABLE_CONTAINER_ID}>
      <InfiniteScrollComponent
        dataLength={notifications.length}
        hasMore={canFetchMore}
        loader={
          isError ? null : (
            <Box margin="var(--layout-spoon)">
              <LoadingItem />
            </Box>
          )
        }
        next={handleInfiniteScroll}
        scrollableTarget={SCROLLABLE_CONTAINER_ID}
      >
        {notifications.map((notification) => {
          const NotificationItem = ITEM_COMPONENTS[notification.dashboardAction.type] || ItemView;
          return (
            <ListItem key={notification.eventId}>
              <NotificationItem
                closeList={closeList}
                key={notification.eventId}
                notification={notification}
              />
            </ListItem>
          );
        })}
      </InfiniteScrollComponent>
      {isError && (
        <ErrorWrapper data-testid={LIST_VIEW_TEST_IDS.infiniteScrollErrorMessage}>
          <Box marginBottom="var(--layout-stamp)">
            <Text color="var(--base-text-secondary)">Couldn't load notifications</Text>
          </Box>
          <SecondaryButton
            data-testid={LIST_VIEW_TEST_IDS.infiniteScrollReloadButton}
            onClick={handleReloadButtonClick}
          >
            Reload
          </SecondaryButton>
        </ErrorWrapper>
      )}
    </Wrapper>
  );
};
